import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiSearch, FiX } from 'react-icons/fi';
import { client, urlFor } from '../../sanity/client';
import SEO from '../SEO/SEO';
import { BreadcrumbSchema } from '../SEO/StructuredData';
import BlogHeader from './BlogHeader';

interface Post {
  _id: string;
  title: string;
  slug: { current: string };
  mainImage: any;
  publishedAt: string;
  excerpt: string;
}

type IconComponent = React.ComponentType<{ className?: string }>;
const ArrowIcon = FiArrowRight as IconComponent;
const SearchIcon = FiSearch as IconComponent;
const ClearIcon = FiX as IconComponent;

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const trimExcerpt = (text: string, maxLength = 140) => {
  const normalized = text?.replace(/\s+/g, ' ').trim() || '';
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}...`;
};

const trimTitle = (text: string, maxLength = 58) => {
  const normalized = text?.replace(/\s+/g, ' ').trim() || '';
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}...`;
};

const BlogCard: React.FC<{ post: Post }> = ({ post }) => (
  <Link
    to={`/blogs/${post.slug.current}`}
    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-teal-200 hover:shadow-xl"
  >
    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
      {post.mainImage ? (
        <img
          src={urlFor(post.mainImage).width(800).url()}
          alt={post.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-slate-100 to-slate-200" />
      )}
    </div>
    <div className="flex flex-1 flex-col p-6">
      <h3 className="line-clamp-1 text-xl font-semibold leading-tight text-slate-900 transition-colors duration-300 group-hover:text-teal-700">
        {trimTitle(post.title)}
      </h3>
      <p className="mt-2 line-clamp-2 min-h-[2.8rem] text-sm leading-6 text-slate-600">
        {trimExcerpt(post.excerpt)}
      </p>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="inline-flex items-center text-sm font-semibold text-teal-700">
          Read article
        </span>
        <ArrowIcon className="h-4 w-4 text-teal-700 transition-transform duration-300 group-hover:translate-x-1" />
      </div>
      <span className="sr-only">
        Open blog post: {post.title}
      </span>
    </div>
  </Link>
);

const BlogSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
    {[1, 2, 3, 4, 5, 6].map((item) => (
      <div key={item} className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="aspect-[16/10] bg-slate-200" />
        <div className="space-y-3 p-6">
          <div className="h-3 w-24 rounded bg-slate-200" />
          <div className="h-6 w-4/5 rounded bg-slate-200" />
          <div className="h-4 w-full rounded bg-slate-200" />
          <div className="h-4 w-5/6 rounded bg-slate-200" />
        </div>
      </div>
    ))}
  </div>
);

const BlogList: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const query = `*[_type == "post"] | order(publishedAt desc) {
  _id,
  title,
  slug,
  mainImage,
  publishedAt,
  excerpt
}`;

    client
      .fetch(query)
      .then((data: Post[]) => {
        setPosts(data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredPosts = posts.filter((post) => {
    if (!normalizedSearch) return true;
    const title = post.title?.toLowerCase() || '';
    const excerpt = post.excerpt?.toLowerCase() || '';
    return title.includes(normalizedSearch) || excerpt.includes(normalizedSearch);
  });

  return (
    <>
      <SEO
        title="Blog"
        description="Read the latest articles and insights from Cool & Care about IT security, HVAC, fire safety, software development, and technology trends in DRC."
        keywords="Cool & Care blog, technology articles, IT security insights, HVAC tips DRC"
      />
      <BreadcrumbSchema items={[{ name: 'Blog', url: '/blogs' }]} />
      <BlogHeader />
      <section className="bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 flex justify-end">
            <div className="relative w-full sm:w-[22rem] lg:w-[24rem]">
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search blogs..."
                className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-11 text-sm text-slate-700 shadow-sm outline-none transition focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <ClearIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <BlogSkeleton />
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post) => (
                <BlogCard key={post._id} post={post} />
              ))}
            </div>
          )}
          {!isLoading && posts.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-600">
              No blog posts found.
            </div>
          )}
          {!isLoading && posts.length > 0 && filteredPosts.length === 0 && (
            <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                <SearchIcon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No matching blogs found</h3>
              <p className="mt-2 text-sm text-slate-600">
                We could not find any blog for <span className="font-medium text-slate-800">"{searchTerm}"</span>.
                Try another keyword.
              </p>
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="mt-5 inline-flex items-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700"
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogList;
